import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2, Upload, FileText, Trash2, Eye, AlertTriangle, Check,
  RefreshCcw, FileType, Plus, UploadCloud,
} from 'lucide-react'
import { notify } from './Toast'
import {
  listKnowledgeSources, uploadKnowledgeSource, deleteKnowledgeSource,
  previewKnowledgeSource, type KnowledgeSourceView,
} from '../api/client'

interface Props {
  personaId: string | null
  /**
   * "card" (default) renders a self-contained wizard-card surface.
   * "bare" renders without the outer wrapper so the parent can compose the
   * panel inside a larger Knowledge card alongside the freeform knowledge-base
   * textarea.
   */
  chrome?: 'card' | 'bare'
  /**
   * When {@link personaId} is null, staging files client-side before the
   * persona exists on the server. Parent flushes these to the real upload
   * endpoint once createPersona returns an id. Omit to keep the legacy
   * "save first" behaviour.
   */
  pendingFiles?: File[]
  onPendingFilesChange?: (files: File[]) => void
}

// Client-side limits when staging before persona creation. The server
// enforces the real tier-scoped limits once the persona exists; these are
// just fail-fast guards so the user doesn't queue up a 1 GB PDF.
const STAGE_MAX_BYTES_PER_FILE = 10 * 1024 * 1024
const STAGE_MAX_FILES = 10

/**
 * Knowledge sources panel shown in the Scope step of the persona wizard.
 * Supports drag-and-drop upload, header counter, and composition inside a
 * parent card via the {@code chrome="bare"} prop.
 */
export default function KnowledgeSourcesPanel({
  personaId, chrome = 'card',
  pendingFiles, onPendingFilesChange,
}: Props) {
  const qc = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [previewing, setPreviewing] = useState<KnowledgeSourceView | null>(null)
  const [dragging, setDragging] = useState(false)

  // ── Create flow — persona not saved yet. ───────────────────────────────
  // If the parent wired up pendingFiles + onPendingFilesChange we accept
  // drops into an in-memory queue; the parent flushes these to the real
  // upload endpoint after createPersona returns an id. Otherwise fall back
  // to the legacy "save first" helper card.
  if (!personaId) {
    if (onPendingFilesChange) {
      return (
        <StagingView
          chrome={chrome}
          files={pendingFiles ?? []}
          onChange={onPendingFilesChange}
        />
      )
    }
    const body = (
      <div className="rounded-xl border border-dashed border-[rgb(var(--border-subtle))] p-6 text-center text-sm text-[rgb(var(--text-muted))]">
        Save your persona first to attach files.
      </div>
    )
    return chrome === 'card' ? (
      <div className="wizard-card">
        <HeaderBlock />
        {body}
      </div>
    ) : <>{body}</>
  }

  const { data, isLoading } = useQuery({
    queryKey: ['knowledge-sources', personaId],
    queryFn: () => listKnowledgeSources(personaId),
  })

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadKnowledgeSource(personaId, file),
    onSuccess: () => {
      notify.success('Source uploaded. Re-consent on the Review step to relaunch.')
      qc.invalidateQueries({ queryKey: ['knowledge-sources', personaId] })
      qc.invalidateQueries({ queryKey: ['my-personas'] })
    },
    onError: (err: { message?: string }) => notify.error(err?.message || 'Upload failed.'),
  })

  const deleteMut = useMutation({
    mutationFn: (sourceId: string) => deleteKnowledgeSource(personaId, sourceId),
    onSuccess: () => {
      notify.success('Source removed. Re-consent on the Review step to relaunch.')
      qc.invalidateQueries({ queryKey: ['knowledge-sources', personaId] })
      qc.invalidateQueries({ queryKey: ['my-personas'] })
    },
    onError: (err: { message?: string }) => notify.error(err?.message || 'Unable to remove source.'),
  })

  const sources = data?.sources ?? []
  const maxSources = data?.maxSources ?? 0
  const featureEnabled = data?.featureEnabled ?? false
  const totalBytes = data?.totalBytes ?? 0
  const atCap = maxSources > 0 && sources.length >= maxSources

  const humanMax = useMemo(() => humanBytes(data?.maxBytesPerFile ?? 0), [data?.maxBytesPerFile])
  const humanTotalMax = useMemo(() => humanBytes(data?.maxTotalBytes ?? 0), [data?.maxTotalBytes])

  const canUpload = featureEnabled && !atCap && !uploadMut.isPending
  const openPicker = () => { if (canUpload) fileInput.current?.click() }

  const handleFile = (file: File) => {
    if (!featureEnabled) {
      notify.error('Upgrade your plan to add knowledge sources.')
      return
    }
    if (atCap) {
      notify.error(`Source limit reached (${maxSources}). Remove one or upgrade your plan.`)
      return
    }
    if (data && file.size > data.maxBytesPerFile) {
      notify.error(`File is too large — up to ${humanMax} per file.`)
      return
    }
    uploadMut.mutate(file)
  }

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) handleFile(file)
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!dragging) setDragging(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    // Ignore drags that enter child elements — only clear on leaving the panel.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setDragging(false)
  }

  const hiddenInput = (
    <input
      ref={fileInput}
      type="file"
      accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
      className="hidden"
      onChange={onFilePicked}
    />
  )

  const header = (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <HeaderBlock />
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {featureEnabled && (
          <div className="flex flex-col items-end text-[11px] tabular-nums leading-tight">
            <span className="text-[rgb(var(--text-primary))] font-medium">
              {sources.length} <span className="text-[rgb(var(--text-muted))] font-normal">of {maxSources}</span>
            </span>
            <span className="text-[rgb(var(--text-muted))]">
              {humanBytes(totalBytes)} <span className="opacity-60">/ {humanTotalMax}</span>
            </span>
          </div>
        )}
        {sources.length > 0 && (
          <button
            type="button"
            onClick={openPicker}
            disabled={!canUpload}
            className="btn-secondary text-sm"
          >
            {uploadMut.isPending
              ? <><Loader2 size={13} className="animate-spin" /> Uploading…</>
              : <><Plus size={13} /> Add source</>}
          </button>
        )}
      </div>
    </div>
  )

  const dropzone = (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={openPicker}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker() }}
      aria-label="Upload a knowledge source"
      className={`
        relative rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent)/0.5)]
        ${dragging
          ? 'border-[rgb(var(--accent))] bg-[rgb(var(--accent)/0.08)]'
          : 'border-[rgb(var(--border-subtle))] hover:border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--surface-2))]'}
        ${!canUpload ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''}
      `}
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[rgb(var(--surface-2))] text-[rgb(var(--text-muted))] mb-3">
        <UploadCloud size={22} />
      </div>
      <p className="text-sm font-medium text-[rgb(var(--text-primary))]">
        {uploadMut.isPending ? 'Uploading…' : dragging ? 'Drop to upload' : 'Drop a file or click to browse'}
      </p>
      <p className="text-[11px] text-[rgb(var(--text-muted))] mt-1">
        PDF · DOCX · TXT · MD · up to {humanMax} each
      </p>
    </div>
  )

  const listView = (
    <ul className="space-y-2">
      {sources.map((s) => (
        <SourceRow
          key={s.id}
          source={s}
          onPreview={() => setPreviewing(s)}
          onDelete={() => {
            if (confirm(`Remove "${s.filename}"? Your persona will need re-consent before relaunching.`)) {
              deleteMut.mutate(s.id)
            }
          }}
          isDeleting={deleteMut.isPending && deleteMut.variables === s.id}
        />
      ))}
    </ul>
  )

  const atCapStrip = atCap && (
    <p className="text-xs text-[rgb(var(--text-muted))]">
      Limit reached —{' '}
      <Link to="/plan" className="text-brand-400 hover:text-brand-300 inline-flex items-center gap-1">
        upgrade for more
      </Link>
    </p>
  )

  const failedHint = sources.some((s) => s.status === 'FAILED') && (
    <p className="text-xs text-amber-400 inline-flex items-center gap-1">
      <AlertTriangle size={12} /> Some sources failed to process
    </p>
  )

  const content = (
    <div
      className="space-y-3"
      onDragOver={sources.length > 0 ? onDragOver : undefined}
      onDragLeave={sources.length > 0 ? onDragLeave : undefined}
      onDrop={sources.length > 0 ? onDrop : undefined}
    >
      {isLoading ? (
        <div className="rounded-xl border border-[rgb(var(--border-subtle))] p-8 flex items-center justify-center text-[rgb(var(--text-muted))]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : sources.length === 0 ? (
        dropzone
      ) : (
        <>
          {listView}
          {!atCap && (
            <div
              onClick={openPicker}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker() }}
              aria-label="Upload another knowledge source"
              className={`
                rounded-lg border-2 border-dashed border-[rgb(var(--border-subtle))] px-3 py-3
                flex items-center justify-center gap-2 text-xs text-[rgb(var(--text-muted))]
                hover:border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--surface-2))] transition-colors cursor-pointer
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent)/0.5)]
                ${dragging ? 'border-[rgb(var(--accent))] bg-[rgb(var(--accent)/0.08)]' : ''}
              `}
            >
              <Upload size={12} />
              {dragging ? 'Drop to upload' : 'Drop another file or click to browse'}
            </div>
          )}
        </>
      )}

      {(atCapStrip || failedHint) && (
        <div className="flex flex-wrap gap-3">
          {atCapStrip}
          {failedHint}
        </div>
      )}

      {hiddenInput}

      {previewing && (
        <PreviewModal
          personaId={personaId}
          source={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  )

  if (chrome === 'bare') {
    return (
      <div className="space-y-4">
        {header}
        {content}
      </div>
    )
  }

  return (
    <div className="wizard-card">
      {header}
      {content}
    </div>
  )
}

function HeaderBlock() {
  return (
    <>
      <div className="wizard-card__eyebrow">Knowledge sources</div>
      <p className="wizard-card__subtitle mt-1">
        Upload documents the AI can reference — PDFs, Word docs, plain text, or markdown.
      </p>
    </>
  )
}

function SourceRow({
  source, onPreview, onDelete, isDeleting,
}: {
  source: KnowledgeSourceView
  onPreview: () => void
  onDelete: () => void
  isDeleting: boolean
}) {
  const Icon = iconFor(source)
  return (
    <li className="flex items-center gap-3 p-3 rounded-lg border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))]">
      <div className="w-9 h-9 rounded-lg bg-[rgb(var(--surface-3))] flex items-center justify-center flex-shrink-0 text-[rgb(var(--text-secondary))]">
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm font-medium text-[rgb(var(--text-primary))] truncate">{source.filename}</span>
          <StatusChip status={source.status} />
          {source.truncated && (
            <span className="text-[10px] px-1.5 h-5 inline-flex items-center rounded bg-amber-500/15 text-amber-400">truncated</span>
          )}
        </div>
        <div className="text-[11px] text-[rgb(var(--text-muted))] mt-0.5">
          {humanBytes(source.sizeBytes)} · {source.extractedBytes > 0 ? `${source.extractedBytes.toLocaleString()} chars extracted` : 'no text'}
          {source.errorMessage ? <> · <span className="text-red-400">{source.errorMessage}</span></> : null}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={onPreview}
          disabled={source.status !== 'READY'}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--surface-3))] transition-colors disabled:opacity-40"
          aria-label={`Preview ${source.filename}`}
          title="View extracted text"
        >
          <Eye size={14} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[rgb(var(--text-muted))] hover:text-red-400 hover:bg-red-500/10 transition-colors"
          aria-label={`Remove ${source.filename}`}
          title="Remove source"
        >
          {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
        </button>
      </div>
    </li>
  )
}

function StatusChip({ status }: { status: KnowledgeSourceView['status'] }) {
  if (status === 'READY') {
    return (
      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-semibold uppercase bg-emerald-500/15 text-emerald-400">
        <Check size={10} /> Ready
      </span>
    )
  }
  if (status === 'PROCESSING') {
    return (
      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-semibold uppercase bg-amber-500/15 text-amber-400">
        <RefreshCcw size={10} className="animate-spin" /> Processing
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-semibold uppercase bg-red-500/15 text-red-400">
      <AlertTriangle size={10} /> Failed
    </span>
  )
}

function PreviewModal({ personaId, source, onClose }: {
  personaId: string
  source: KnowledgeSourceView
  onClose: () => void
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['knowledge-source-preview', personaId, source.id],
    queryFn: () => previewKnowledgeSource(personaId, source.id),
  })

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="modal-panel relative w-full max-w-3xl rounded-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[rgb(var(--border-subtle))]">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-100 truncate">
              {source.filename}
            </h3>
            <p className="text-xs text-[rgb(var(--text-muted))] mt-0.5">
              {humanBytes(source.sizeBytes)} · {data?.extractedBytes.toLocaleString() || '—'} characters extracted
              {data?.truncated && ' · truncated'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="nav-icon-btn flex-shrink-0" aria-label="Close preview">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {isLoading ? (
            <div className="h-40 flex items-center justify-center text-[rgb(var(--text-muted))]">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[rgb(var(--text-secondary))] font-sans">
              {data?.extractedText || <span className="italic text-[rgb(var(--text-muted))]">No text extracted.</span>}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Pre-save staging UI. Files are held in memory on the parent; the real
 * upload happens after createPersona returns an id. Same drop/pick UX as
 * the post-save view but with a "queued — will upload after save" chip and
 * no server round-trips.
 */
function StagingView({
  chrome, files, onChange,
}: {
  chrome: 'card' | 'bare'
  files: File[]
  onChange: (next: File[]) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const totalBytes = files.reduce((sum, f) => sum + f.size, 0)
  const atCap = files.length >= STAGE_MAX_FILES

  const accept = (file: File) => {
    if (atCap) {
      notify.error(`Queue full — up to ${STAGE_MAX_FILES} files pending. Save the persona first to add more.`)
      return
    }
    if (file.size > STAGE_MAX_BYTES_PER_FILE) {
      notify.error(`File too large — up to ${humanBytes(STAGE_MAX_BYTES_PER_FILE)} per file.`)
      return
    }
    // Dedupe by name + size so dragging the same file twice doesn't double-queue.
    if (files.some((f) => f.name === file.name && f.size === file.size)) {
      notify.info('That file is already queued.')
      return
    }
    onChange([...files, file])
  }

  const removeAt = (idx: number) => onChange(files.filter((_, i) => i !== idx))
  const openPicker = () => { if (!atCap) fileInput.current?.click() }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    Array.from(e.dataTransfer.files || []).forEach(accept)
  }
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); if (!dragging) setDragging(true) }
  const onDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setDragging(false)
  }

  const hidden = (
    <input
      ref={fileInput}
      type="file"
      multiple
      accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
      className="hidden"
      onChange={(e) => {
        Array.from(e.target.files || []).forEach(accept)
        e.target.value = ''
      }}
    />
  )

  const header = (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <HeaderBlock />
      {files.length > 0 && (
        <div className="flex flex-col items-end text-[11px] tabular-nums leading-tight">
          <span className="text-[rgb(var(--text-primary))] font-medium">
            {files.length} <span className="text-[rgb(var(--text-muted))] font-normal">queued</span>
          </span>
          <span className="text-[rgb(var(--text-muted))]">{humanBytes(totalBytes)}</span>
        </div>
      )}
    </div>
  )

  const dropzone = (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={openPicker}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker() }}
      aria-label="Upload knowledge sources"
      className={`
        relative rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent)/0.5)]
        ${dragging
          ? 'border-[rgb(var(--accent))] bg-[rgb(var(--accent)/0.08)]'
          : 'border-[rgb(var(--border-subtle))] hover:border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--surface-2))]'}
        ${atCap ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''}
      `}
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[rgb(var(--surface-2))] text-[rgb(var(--text-muted))] mb-3">
        <UploadCloud size={22} />
      </div>
      <p className="text-sm font-medium text-[rgb(var(--text-primary))]">
        {dragging ? 'Drop to queue' : 'Drop files or click to browse'}
      </p>
      <p className="text-[11px] text-[rgb(var(--text-muted))] mt-1">
        PDF · DOCX · TXT · MD · up to {humanBytes(STAGE_MAX_BYTES_PER_FILE)} each · uploaded on save
      </p>
    </div>
  )

  const list = (
    <ul className="space-y-2">
      {files.map((f, i) => (
        <li key={`${f.name}-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))]">
          <div className="w-9 h-9 rounded-lg bg-[rgb(var(--surface-3))] flex items-center justify-center flex-shrink-0 text-[rgb(var(--text-secondary))]">
            <FileText size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-sm font-medium text-[rgb(var(--text-primary))] truncate">{f.name}</span>
              <span className="text-[10px] px-1.5 h-5 inline-flex items-center gap-1 rounded bg-amber-500/15 text-amber-400 font-semibold uppercase">
                Queued
              </span>
            </div>
            <div className="text-[11px] text-[rgb(var(--text-muted))] mt-0.5">
              {humanBytes(f.size)} · will upload when you save
            </div>
          </div>
          <button
            type="button"
            onClick={() => removeAt(i)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[rgb(var(--text-muted))] hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
            aria-label={`Remove queued ${f.name}`}
            title="Remove from queue"
          >
            <Trash2 size={14} />
          </button>
        </li>
      ))}
    </ul>
  )

  const addMore = files.length > 0 && !atCap && (
    <div
      onClick={openPicker}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker() }}
      aria-label="Queue another file"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`
        rounded-lg border-2 border-dashed border-[rgb(var(--border-subtle))] px-3 py-3
        flex items-center justify-center gap-2 text-xs text-[rgb(var(--text-muted))]
        hover:border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--surface-2))] transition-colors cursor-pointer
        ${dragging ? 'border-[rgb(var(--accent))] bg-[rgb(var(--accent)/0.08)]' : ''}
      `}
    >
      <Upload size={12} />
      {dragging ? 'Drop to queue' : 'Drop another file or click to browse'}
    </div>
  )

  const content = (
    <div className="space-y-3">
      {files.length === 0 ? dropzone : (<>{list}{addMore}</>)}
      {hidden}
    </div>
  )

  if (chrome === 'bare') {
    return (
      <div className="space-y-4">
        {header}
        {content}
      </div>
    )
  }
  return (
    <div className="wizard-card">
      {header}
      {content}
    </div>
  )
}

function iconFor(s: KnowledgeSourceView) {
  const name = (s.filename || '').toLowerCase()
  if (name.endsWith('.pdf')) return FileText
  if (name.endsWith('.docx')) return FileType
  return FileText
}

function humanBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}
